import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, ChevronLeft, ChevronRight, Download } from "lucide-react";
import type { Fill } from "@/lib/hyperliquid";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { exportFillsToCSV, type ExportContext } from "@/lib/csvExport";
import { useWallet } from "@/hooks/useWallet";
import { useToast } from "@/hooks/use-toast";

interface RecentFillsTableProps {
  fills?: Fill[];
  loading?: boolean;
  className?: string;
  exportContext?: ExportContext;
  rangeDays?: number;
}

export const RecentFillsTable = ({ 
  fills = [], 
  loading = false,
  className,
  exportContext,
  rangeDays = 30
}: RecentFillsTableProps) => {
  const { address } = useWallet();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const pageSize = 15;
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(fills.length / pageSize));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(p, 1), totalPages));
  }, [totalPages]);

  const pageFills = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * pageSize;
    return fills.slice(start, start + pageSize);
  }, [fills, page, totalPages]);

  if (loading) {
    return (
      <div className={cn("stat-card overflow-hidden", className)}>
        <div className="flex items-center justify-between mb-6">
          <div className="h-5 w-32 bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-20 bg-muted/50 rounded animate-pulse" />
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 w-full bg-muted/30 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const formatTime = (ts: number) => {
    const date = new Date(ts);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleExport = async () => {
    if (!address || !exportContext) {
      toast({
        title: "Export Error",
        description: "Wallet address or export context not available",
        variant: "destructive"
      });
      return;
    }

    setIsExporting(true);
    try {
      await exportFillsToCSV({ 
        address, 
        context: exportContext, 
        rangeDays 
      });
      
      toast({
        title: "Export Successful",
        description: `Your ${exportContext === 'dashboard' ? 'combined' : exportContext} fills have been exported to CSV.`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "An unknown error occurred during export.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (fills.length === 0) {
    return (
      <div className={cn("stat-card overflow-hidden", className)}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-base font-semibold">Recent Fills</h3>
        </div>
        <p className="text-muted-foreground text-center py-12 text-sm">
          No fills found. Click "Sync" to fetch your trading history.
        </p>
      </div>
    );
  }

  return (
    <div className={cn("stat-card overflow-hidden p-0 group", className)}>
      <div className="relative z-10">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border/50">
          <h3 className="text-base font-semibold">Recent Fills</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{fills.length} trades</span>
            <span className="text-xs text-muted-foreground">Page {page} / {totalPages}</span>
            {exportContext && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExport}
                disabled={isExporting || fills.length === 0}
                className="h-7 px-2 text-xs"
              >
                <Download className="w-3 h-3 mr-1" />
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            )}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse"></div>
          </div>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Symbol</th>
                <th>Side</th>
                <th className="text-right">Size</th>
                <th className="text-right">Price</th>
                <th className="text-right">Notional</th>
                <th className="text-right">Fee</th>
              </tr>
            </thead>
            <tbody>
              {pageFills.map((fill, index) => {
                const side = fill.side?.toLowerCase();
                const isBuy = side === 'b' || side === 'buy';
                const symbol = fill.symbol || fill.coin || '-';
                const price = fill.px ?? 0;
                const size = fill.sz ?? 0;
                const notional = fill.notional ?? (price * size);
                
                return (
                  <tr key={index}>
                    <td className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {formatTime(fill.fill_time_ms)}
                    </td>
                    <td>
                      <span className="font-semibold text-accent">{symbol}</span>
                    </td>
                    <td>
                      <span className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border",
                        isBuy 
                          ? "bg-primary/10 text-primary border-primary/20" 
                          : "bg-destructive/10 text-destructive border-destructive/20"
                      )}>
                        {isBuy ? (
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowDownRight className="w-3.5 h-3.5" />
                        )}
                        {isBuy ? 'BUY' : 'SELL'}
                      </span>
                    </td>
                    <td className="text-right font-mono text-sm font-medium">{size.toLocaleString()}</td>
                    <td className="text-right font-mono text-sm">${price.toLocaleString()}</td>
                    <td className="text-right font-mono text-sm font-medium">${notional.toLocaleString()}</td>
                    <td className="text-right font-mono text-xs text-muted-foreground">
                      {fill.fee != null ? `$${Number(fill.fee).toFixed(2)}` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
